import { ArrowRight } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { PublicConfig } from "../../shared/types";
import { submissionTextSchema } from "../../shared/validation";
import { ApiError, createSubmission, getConfig } from "../api";
import { CameraCapture } from "../components/CameraCapture";
import { KineticIntro } from "../components/KineticIntro";
import { SiteFooter } from "../components/SiteFooter";
import { Turnstile, type TurnstileState } from "../components/Turnstile";

const getDeviceId = (): string => {
  const key = "madonna-device-id";
  const stored = localStorage.getItem(key);
  if (stored) return stored;
  const next = crypto.randomUUID();
  localStorage.setItem(key, next);
  return next;
};

export function SubmitPage() {
  const [intro, setIntro] = useState(true);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileVersion, setTurnstileVersion] = useState(0);
  const [turnstileState, setTurnstileState] = useState<TurnstileState>("loading");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getConfig().then(setConfig).catch(() => setError("The experience is temporarily unavailable."));
  }, []);

  const validation = useMemo(() => submissionTextSchema.safeParse({ name, email, message }), [email, name, message]);
  const finishIntro = useCallback(() => setIntro(false), []);
  const onToken = useCallback((token: string) => setTurnstileToken(token), []);
  const onTurnstileState = useCallback((state: TurnstileState) => setTurnstileState(state), []);
  const restartSecurityCheck = useCallback(() => {
    setTurnstileToken("");
    setTurnstileState("loading");
    setTurnstileVersion((current) => current + 1);
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!validation.success) {
      setError(validation.error.issues[0]?.message ?? "Please check your name and message.");
      return;
    }
    if (!termsAccepted) {
      setError("Please confirm that you are 18 or over and accept the Terms & Conditions and Privacy Policy.");
      return;
    }
    if (!config) {
      setError("The experience is still loading. Please try again in a moment.");
      return;
    }
    if (!turnstileToken) {
      setError(
        turnstileState === "loading"
          ? "The security check is still loading. Please wait a moment and try again."
          : "The security check needs to run again. Please try once more.",
      );
      if (turnstileState === "expired" || turnstileState === "error") restartSecurityCheck();
      return;
    }

    setSubmitting(true);
    const body = new FormData();
    body.set("name", validation.data.name);
    if (validation.data.email) body.set("email", validation.data.email);
    body.set("message", validation.data.message);
    body.set("deviceId", getDeviceId());
    body.set("turnstileToken", turnstileToken);
    body.set("termsAccepted", "true");
    body.set("dataConsent", "true");
    body.set("consentVersion", "2026-08-05");
    if (photo) body.set("photo", photo, "camera-selfie.jpg");

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 45_000);
    try {
      const result = await createSubmission(body, controller.signal);
      if (!result.statusToken) throw new Error("Your private result link could not be created.");
      // Both text-only and portrait submissions use the same private result page.
      window.location.replace(`/status/${result.statusToken}`);
      return;
    } catch (caught) {
      setError(
        caught instanceof DOMException && caught.name === "AbortError"
          ? "The connection took too long. Your message was not sent. Please try again."
          : caught instanceof ApiError
            ? caught.message
            : "Something went wrong. Please try again.",
      );
      restartSecurityCheck();
    } finally {
      window.clearTimeout(timeout);
      setSubmitting(false);
    }
  };

  if (intro) return <KineticIntro onComplete={finishIntro} />;

  return (
    <>
    <main className="submit-page page-enter">
      <figure className="submit-feature-image">
        <img
          src="/audience-form-hero.jpg"
          alt="Pink and red dance-floor portrait"
          width="1920"
          height="1133"
          decoding="async"
        />
      </figure>
      <header className="submit-hero">
        <h1><span>C’mon</span><span>meet me on the</span><span>dancefloor</span></h1>
        <p>Share your confession or a birthday message for Madonna — and say it out loud on the big Outernet screens later today.</p>
      </header>

      <form onSubmit={submit} noValidate>
        <section className="form-section form-section--pink" aria-label="Your message">
          <label className="field">
            <span>Your name <b aria-hidden="true">*</b></span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={30}
              autoComplete="name"
              placeholder="YOUR NAME"
              required
            />
            <small>{name.length} / 30</small>
          </label>

          <label className="field field--email">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              maxLength={254}
              autoComplete="email"
              inputMode="email"
              placeholder="YOU@EXAMPLE.COM"
            />
          </label>

          <label className="field">
            <span>Let’s hear your message <b aria-hidden="true">*</b></span>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value.replace(/[\r\n]/g, " "))}
              maxLength={200}
              rows={5}
              placeholder="EVERYONE HERE IS A WORK OF ART"
              required
            />
            <small>{message.length} / 200</small>
          </label>
          <p className="field-help">Latin letters and basic punctuation only. No emoji, links or handles.</p>
        </section>

        <CameraCapture value={photo} onChange={setPhoto} />

        <section className="form-section form-section--white" aria-labelledby="send-title">
          <span className="eyebrow">Send</span>
          <h2 id="send-title">Ready for the<br />dance floor?</h2>
          <label className="consent">
            <input type="checkbox" required checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} />
            <span>
              I confirm that I am 18 or over, and I agree to the <a href="/terms#terms" target="_blank" rel="noreferrer">Terms &amp; Conditions</a> and <a href="/terms#privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </span>
          </label>
          {config && (
            <Turnstile
              key={turnstileVersion}
              siteKey={config.turnstileSiteKey}
              onToken={onToken}
              onState={onTurnstileState}
            />
          )}
          <div className={`security-status security-status--${turnstileState}`} aria-live="polite">
            {turnstileState === "loading" && <span>Preparing secure submission…</span>}
            {turnstileState === "verified" && <span>Secure submission ready.</span>}
            {(turnstileState === "expired" || turnstileState === "error") && (
              <>
                <span>Security check needs to run again.</span>
                <button type="button" onClick={restartSecurityCheck}>Retry check</button>
              </>
            )}
          </div>
          {error && <p className="submit-error" role="alert">{error}</p>}
          <button className="submit-button" type="submit" disabled={submitting}>
            <span>{submitting ? "Sending…" : "Send to the screens"}</span>
            <ArrowRight size={34} weight="bold" aria-hidden="true" />
          </button>
        </section>
      </form>
    </main>
    <SiteFooter />
    </>
  );
}
